// -----------------------------------------------------------------------------
// TaskAssigneeRow  (Task #52)
//
// Inline assignee row for a single guided-checklist task.
//
// Display:
//   • Up to 3 stacked initial/avatar circles (28px) + "+N" chip for overflow
//   • "Assign" icon-button when no assignees set
//
// Interaction:
//   • Click any avatar or the assign button → opens a Popper anchored to
//     the row with AssignedToLead multi-select + "Save" / "Cancel" buttons
//   • Save → PATCH /api/v1/practices/{practiceId}/tasks/{taskId}/
//             body: { assignees: [{ practice_user_id: N }] }
//
// Cross-referencing:
//   The task-checklists API returns assignees as { id: user_id, name }.
//   AssignedToLead / usePracticeStaff work with PracticeUser objects whose
//   pu.user.id === user_id.  On mount we cross-reference to hydrate the
//   initial selection with full PracticeUser objects so the picker shows
//   which users are already selected.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useMemo, useCallback } from 'react';
import {
    Box, Typography, Tooltip, Button, Stack,
    Popper, Paper, ClickAwayListener, CircularProgress,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import AssignedToLead from 'src/components/AssignedToLead';
import usePracticeStaff from 'src/hooks/usePracticeStaff';
import { callApi } from 'src/utils/apiCall';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';

// ---------------------------------------------------------------------------
// Avatar circle — shows photo if available, else initials
// ---------------------------------------------------------------------------
function StaffAvatar({ user, size = 24 }) {
    const initials = (
        ((user?._initials) || ((user?.user?.first_name?.[0] || '') + (user?.user?.last_name?.[0] || '')).toUpperCase()) || '?'
    );
    return (
        <Tooltip
            title={user?._name || `${user?.user?.first_name || ''} ${user?.user?.last_name || ''}`.trim()}
            arrow
            placement="top"
        >
            <Box
                sx={{
                    width: size,
                    height: size,
                    borderRadius: '50%',
                    bgcolor: '#078DEE22',
                    border: '1.5px solid #fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: `${Math.max(8, size * 0.38)}px`,
                    fontWeight: 700,
                    color: '#078DEE',
                    overflow: 'hidden',
                    flexShrink: 0,
                    ml: '-4px',
                    '&:first-of-type': { ml: 0 },
                }}
            >
                {user?.user?.avatar
                    ? <img src={user.user.avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                    : initials
                }
            </Box>
        </Tooltip>
    );
}
StaffAvatar.propTypes = {
    user: PropTypes.object,
    size: PropTypes.number,
};
StaffAvatar.defaultProps = { user: null, size: 24 };

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function TaskAssigneeRow({ practiceId, taskId, initialAssignees, size, readOnly, onSaved }) {
    const { assignableUsers, isLoaded } = usePracticeStaff();
    const [popperAnchor, setPopperAnchor] = useState(null);
    const [saving, setSaving] = useState(false);

    // ── Hydrate initial selection from practiceUser list ───────────────────
    const hydratedInitial = useMemo(() => {
        if (!isLoaded || !initialAssignees?.length) return [];
        const userIdSet = new Set(initialAssignees.map(a => a.id));
        return assignableUsers.filter(pu => userIdSet.has(pu.user?.id));
    }, [isLoaded, initialAssignees, assignableUsers]);

    const [selected, setSelected] = useState(null); // null = use hydratedInitial (lazy init)

    // Effective selection: once user interacts (selected !== null), use that.
    // Before first open we fall back to hydratedInitial so display is correct.
    const effectiveSelected = selected !== null ? selected : hydratedInitial;

    const openPopper = useCallback((e) => {
        if (readOnly) return;
        // Initialise picker with current hydrated selection on first open
        if (selected === null) setSelected(hydratedInitial);
        setPopperAnchor(e.currentTarget);
    }, [readOnly, selected, hydratedInitial]);

    const closePopper = useCallback(() => setPopperAnchor(null), []);

    const handleSave = async () => {
        if (!taskId || !practiceId) return;
        setSaving(true);
        try {
            const payload = { assignees: effectiveSelected.map(pu => ({ practice_user_id: pu.id })) };
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/`,
                'patch',
                payload,
            );
            if (res.ok) {
                closePopper();
                if (onSaved) onSaved(effectiveSelected);
            } else {
                dispatch(updateError({ success: false, message: res?.data?.detail || 'Failed to update assignees' }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Something went wrong' }));
        } finally {
            setSaving(false);
        }
    };

    const VISIBLE = 3;
    const overflow = effectiveSelected.length > VISIBLE ? effectiveSelected.length - VISIBLE : 0;
    const visible = effectiveSelected.slice(0, VISIBLE);
    const open = Boolean(popperAnchor);

    return (
        <>
            <Box
                data-testid="task-assignee-row"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: readOnly ? 'default' : 'pointer',
                    minHeight: size + 4,
                }}
                onClick={!readOnly ? openPopper : undefined}
            >
                {effectiveSelected.length === 0 ? (
                    /* Empty state: "Assign" prompt */
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            opacity: 0.55,
                            '&:hover': readOnly ? {} : { opacity: 1 },
                            transition: 'opacity 150ms ease',
                        }}
                    >
                        <Iconify icon="mdi:account-plus-outline" width={14} height={14} sx={{ color: '#6e6e73' }} />
                        {size >= 22 && (
                            <Typography sx={{ fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.005em' }}>
                                Assign
                            </Typography>
                        )}
                    </Box>
                ) : (
                    /* Avatar stack */
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {visible.map(pu => <StaffAvatar key={pu.id} user={pu} size={size} />)}
                        {overflow > 0 && (
                            <Box sx={{
                                width: size,
                                height: size,
                                borderRadius: '50%',
                                bgcolor: '#6e6e7322',
                                border: '1.5px solid #fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: `${Math.max(8, size * 0.36)}px`,
                                fontWeight: 700,
                                color: '#6e6e73',
                                ml: '-4px',
                                flexShrink: 0,
                            }}>
                                +{overflow}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Picker Popper */}
            <Popper
                open={open}
                anchorEl={popperAnchor}
                placement="bottom-start"
                modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
                sx={{ zIndex: 1400 }}
            >
                <ClickAwayListener onClickAway={closePopper}>
                    <Paper
                        data-testid="task-assignee-popper"
                        elevation={4}
                        sx={{
                            p: 2,
                            minWidth: 280,
                            borderRadius: '12px',
                            border: '1px solid rgba(0,0,0,0.08)',
                        }}
                    >
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#3c3c43', mb: 1.5, letterSpacing: '-0.01em' }}>
                            Assign team member
                        </Typography>

                        {isLoaded ? (
                            <AssignedToLead
                                assignToState={selected || []}
                                setAssignTo={setSelected}
                                staffRole="assignee"
                            />
                        ) : (
                            <CircularProgress size={20} />
                        )}

                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                            <Button
                                size="small"
                                onClick={closePopper}
                                sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '13px' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                data-testid="task-assignee-save-btn"
                                size="small"
                                variant="contained"
                                disableElevation
                                disabled={saving}
                                onClick={handleSave}
                                sx={{ fontSize: '13px', textTransform: 'none', fontWeight: 600 }}
                            >
                                {saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Save'}
                            </Button>
                        </Stack>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
}

TaskAssigneeRow.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    /** Task UUID — used in PATCH URL. */
    taskId: PropTypes.string,
    /** Raw assignees from task-checklists API: [{ id: user_id, name }] */
    initialAssignees: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    /** Circle diameter in px. Default 24 for featured card; 16 for carousel chips. */
    size: PropTypes.number,
    /** When true, clicking does nothing (next-up pane). */
    readOnly: PropTypes.bool,
    /** Called after a successful PATCH with the new selected[] array. */
    onSaved: PropTypes.func,
};

TaskAssigneeRow.defaultProps = {
    taskId: null,
    initialAssignees: [],
    size: 24,
    readOnly: false,
    onSaved: null,
};
